import { useCallback, useEffect, useMemo, useState } from 'react';
import { InventaireResponse } from '@repo/types';
import { Logger } from '@repo/ui/lib/logger/index';
import { useInventaire } from '../../../hooks';
import { FormData } from '../../../shared/types';
import { InventaireServerResponse, mapServerDataToFormData } from '../../edit/utils/data-mapper';
import { Step } from '../../../shared/types';

interface UseDetailsArbreResult {
  formData: FormData;
  originalData: InventaireResponse | null;
  completedSteps: Set<number>;
  isLoading: boolean;
  isValueCompleted: (value: unknown) => boolean;
  isStepCompleted: (stepIndex: number) => boolean;
  completionPercentage: number;
  completedFieldsCount: number;
}

export function useDetailsArbre(
  inventaireId: number,
  initialData?: InventaireServerResponse,
  steps?: Step[]
): UseDetailsArbreResult {
  const [formData, setFormData] = useState<FormData>({});
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [originalData, setOriginalData] = useState<InventaireResponse | null>(null);

  const { data, isLoading } = useInventaire(inventaireId);

  const isValueCompleted = useCallback((value: unknown): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'number') return value !== null && value !== undefined;
    return value !== null && value !== undefined;
  }, []);

  const isStepCompleted = useCallback(
    (stepIndex: number): boolean => {
      if (!steps || !steps[stepIndex]) return false;
      const step = steps[stepIndex];
      return step.fields.length > 0 && step.fields.every(field => isValueCompleted(formData[field.name]));
    },
    [formData, isValueCompleted, steps]
  );

  const completionPercentage = useMemo(() => {
    if (!steps) return 0;
    const totalFields = steps.reduce((acc, step) => acc + step.fields.length, 0);
    const completedFields = Object.values(formData).filter(isValueCompleted).length;
    return totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  }, [formData, isValueCompleted, steps]);

  const completedFieldsCount = useMemo(
    () => Object.values(formData).filter(isValueCompleted).length,
    [formData, isValueCompleted]
  );

  useEffect(() => {
    if (data) {
      const mappedData = mapServerDataToFormData(data);
      Logger.debug('mappedData', mappedData);
      setFormData(mappedData);
      setOriginalData(data);
    }
  }, [data]);

  useEffect(() => {
    if (!steps) return;
    const newCompletedSteps = new Set<number>();
    steps.forEach((_, index) => {
      if (isStepCompleted(index)) {
        newCompletedSteps.add(index);
      }
    });
    setCompletedSteps(newCompletedSteps);
  }, [formData, isStepCompleted, steps]);

  return {
    formData,
    originalData,
    completedSteps,
    isLoading,
    isValueCompleted,
    isStepCompleted,
    completionPercentage,
    completedFieldsCount,
  };
}

